/**
 * The configuration API module: the terminology override list, live in prod.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * The configuration endpoints are live (M3 Track E). This module carries only what the
 * configuration screen reads today: the terminology list. The decision-rule and
 * calendar-enforcement editors are declared future work and are named as such on the
 * screen; nothing here fakes them.
 */

import { apiJson, bool, isRecord, num, optNum, optStr, str } from "./client";

export interface TerminologyOverride {
  readonly id: string;
  readonly objectKey: string;
  readonly locale: string;
  readonly value: string;
  readonly updatedAt: string;
}

export async function listTerminologyOverrides(
  accessToken: string,
  signal?: AbortSignal,
): Promise<TerminologyOverride[]> {
  return apiJson(
    accessToken,
    "/configuration/terminology",
    (body: unknown): TerminologyOverride[] => {
      if (!Array.isArray(body)) {
        throw new Error("Expected the terminology list");
      }
      return body.map((entry) => {
        const record = entry as Record<string, unknown>;
        return {
          id: String(record.id),
          objectKey: String(record.object_key),
          locale: String(record.locale),
          value: String(record.value),
          updatedAt: String(record.updated_at),
        };
      });
    },
    { signal },
  );
}

/* ---------------------------------------------------------------------- */
/* Wave b12 frontend item 5 (wave COM item 1): the task clocks.             */
/* ---------------------------------------------------------------------- */

/** One task kind's clock, as GET /api/configuration/task-clocks reads it. */
export interface TaskClock {
  readonly id: string;
  readonly taskKind: string;
  /** Hours from the task's opening to its due time; null: the kind has no due time. */
  readonly hoursToDue: number | null;
  /** The share of the clock after which the reminder rings (0 to 1, both exclusive). */
  readonly reminderFraction: number;
  /** Hours after the due time before an overdue task is escalated. */
  readonly escalationHoursAfterDue: number;
  /** The approval-chain rank the escalation goes to; null: the QC lead at tenant. */
  readonly escalateToRank: number | null;
  readonly updatedAt: string;
}

export interface TaskClocks {
  /** How many clocks the last write changed (0 on a read). */
  readonly changed: number;
  /** The tenant's witness grace: hours after the agreed day's start before release. */
  readonly witnessGraceHours: number;
  readonly items: readonly TaskClock[];
}

/** One kind's change on the wire (TaskClockChange): every field named is written. */
export interface TaskClockChange {
  readonly task_kind: string;
  readonly hours_to_due: number | null;
  readonly reminder_fraction: number;
  readonly escalation_hours_after_due: number;
  readonly escalate_to_rank: number | null;
}

function clocksBody(raw: unknown): TaskClocks {
  const body = raw as Record<string, unknown>;
  const items = Array.isArray(body.items) ? body.items : [];
  return {
    changed: typeof body.changed === "number" ? body.changed : 0,
    witnessGraceHours: Number(body.witness_grace_hours),
    items: items.map((entry) => {
      const one = entry as Record<string, unknown>;
      return {
        id: String(one.id),
        taskKind: String(one.task_kind),
        hoursToDue: typeof one.hours_to_due === "number" ? one.hours_to_due : null,
        reminderFraction: Number(one.reminder_fraction),
        escalationHoursAfterDue: Number(one.escalation_hours_after_due),
        escalateToRank: typeof one.escalate_to_rank === "number" ? one.escalate_to_rank : null,
        updatedAt: String(one.updated_at),
      };
    }),
  };
}

export async function readTaskClocks(token: string, signal?: AbortSignal): Promise<TaskClocks> {
  return apiJson(token, "/configuration/task-clocks", clocksBody, { signal });
}

/**
 * PATCH /api/configuration/task-clocks. The server refuses 409 configuration.unchanged
 * when nothing named differs, 422 configuration.kind_repeated when a kind is named twice
 * and 422 configuration.nothing_named for an empty change; each is said in its words.
 */
export async function setTaskClocks(
  token: string,
  change: {
    readonly clocks?: readonly TaskClockChange[];
    readonly witness_grace_hours?: number;
  },
): Promise<TaskClocks> {
  return apiJson(token, "/configuration/task-clocks", clocksBody, {
    method: "PATCH",
    body: change,
  });
}

/* ---------------------------------------------------------------------- */
/* Wave b12 frontend item 6 (wave COM item 5): the webhook endpoints.       */
/* ---------------------------------------------------------------------- */

export const WEBHOOK_EVENT_KINDS = [
  "TASK_OPENED",
  "TASK_CLOSED",
  "BELL",
  "RECORD_STATE_CHANGED",
] as const;

/**
 * One endpoint. `secretRef` NAMES the environment variable that holds the signing secret
 * (QUALIFY_WEBHOOK_SECRET_*); the secret itself never reaches the client, only its
 * fingerprint, so the configurer can tell two secrets apart.
 */
export interface WebhookEndpoint {
  readonly id: string;
  readonly url: string;
  readonly secretRef: string;
  readonly secretFingerprint: string;
  readonly eventKinds: readonly string[];
  readonly active: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
}

function endpoint(raw: unknown): WebhookEndpoint {
  const one = raw as Record<string, unknown>;
  return {
    id: String(one.id),
    url: String(one.url),
    secretRef: String(one.secret_ref),
    secretFingerprint: String(one.secret_fingerprint),
    eventKinds: Array.isArray(one.event_kinds) ? one.event_kinds.map(String) : [],
    active: one.active === true,
    createdAt: String(one.created_at),
    updatedAt: String(one.updated_at),
  };
}

export async function listWebhookEndpoints(
  token: string,
  signal?: AbortSignal,
): Promise<WebhookEndpoint[]> {
  return apiJson(
    token,
    "/configuration/webhook-endpoints",
    (raw) => {
      const body = raw as Record<string, unknown>;
      return (Array.isArray(body.items) ? body.items : []).map(endpoint);
    },
    { signal },
  );
}

export interface WebhookEndpointPayload {
  readonly url: string;
  readonly secret_ref: string;
  readonly event_kinds: readonly string[];
  readonly active: boolean;
}

/** POST: a new endpoint. 422 webhook.private_address names the url field. */
export async function createWebhookEndpoint(
  token: string,
  payload: WebhookEndpointPayload,
): Promise<WebhookEndpoint> {
  return apiJson(token, "/configuration/webhook-endpoints", endpoint, {
    method: "POST",
    body: payload,
  });
}

/** PATCH: only the fields named change. */
export async function updateWebhookEndpoint(
  token: string,
  endpointId: string,
  payload: Partial<WebhookEndpointPayload>,
): Promise<WebhookEndpoint> {
  return apiJson(token, `/configuration/webhook-endpoints/${endpointId}`, endpoint, {
    method: "PATCH",
    body: payload,
  });
}

/* ---------------------------------------------------------------------- */
/* Wave AUD frontend item 4: the configuration audit trail                 */
/* (GET /api/configuration/audit, CONFIGURATION chain only).               */
/* ---------------------------------------------------------------------- */

export interface ConfigurationChainVerdict {
  readonly intact: boolean;
  readonly entriesChecked: number;
  readonly brokenAtSeq: number | null;
  readonly reason: string | null;
}

export interface ConfigurationAuditEntry {
  readonly id: string;
  readonly seq: number;
  readonly eventType: string;
  readonly objectType: string;
  readonly objectId: string | null;
  readonly payload: Readonly<Record<string, unknown>>;
  readonly actorPersonId: string | null;
  readonly actorDisplayName: string | null;
  readonly actorPartyId: string | null;
  readonly actorPartyName: string | null;
  readonly createdAt: string;
}

export interface ConfigurationAudit {
  readonly totalReported: number;
  readonly chain: ConfigurationChainVerdict;
  readonly items: readonly ConfigurationAuditEntry[];
}

export async function readConfigurationAudit(
  token: string,
  signal?: AbortSignal,
): Promise<ConfigurationAudit> {
  return apiJson(
    token,
    "/configuration/audit",
    (raw): ConfigurationAudit => {
      const body = isRecord(raw) ? raw : {};
      const rawChain = isRecord(body.chain) ? body.chain : {};
      const rawItems = Array.isArray(body.items) ? body.items.filter(isRecord) : [];
      const items = rawItems
        .filter((row) => !("family" in row) || row.family === "CONFIGURATION")
        .map((row): ConfigurationAuditEntry => ({
          id: str(row.id),
          seq: num(row.seq),
          eventType: str(row.event_type),
          objectType: str(row.object_type),
          objectId: optStr(row.object_id),
          payload: isRecord(row.payload) ? row.payload : {},
          actorPersonId: optStr(row.actor_person_id),
          actorDisplayName: optStr(row.actor_display_name),
          actorPartyId: optStr(row.actor_party_id),
          actorPartyName: optStr(row.actor_party_name),
          createdAt: str(row.created_at),
        }));
      return {
        totalReported: typeof body.total_reported === "number" ? body.total_reported : items.length,
        chain: {
          intact: typeof rawChain.intact === "boolean" ? bool(rawChain.intact) : true,
          entriesChecked:
            typeof rawChain.entries_checked === "number"
              ? num(rawChain.entries_checked)
              : items.length,
          brokenAtSeq: optNum(rawChain.broken_at_seq),
          reason: optStr(rawChain.reason),
        },
        items,
      };
    },
    { signal },
  );
}
